import {
  ECardinality,
  EProjectType,
  EScreenType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IDisplayItem,
  INavScreenInfo,
  ISymbol,
  ITargetScreenInputParam
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { NavigationScreenInputPicker } from "./navigation-screen-input-picker";
import { PropertyForm } from "./property-form";
import { SymbolPicker } from "./symbol-picker";

export interface INavigationSymbolsGroupState {}

export interface INavigationSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  targetScreenInputParams: ITargetScreenInputParam[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  deleteTargetScreenInputParams: (parameterName: string) => void;
}

class NavigationSymbolsGroup extends PureSdComponent<
  INavigationSymbolsGroupProps & IWithSDProps & IWithContextProp,
  INavigationSymbolsGroupState
> {
  propertySdHelper: PropertySdHelper;

  constructor(props: INavigationSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
  }

  componentDidMount(): void {
    this.applyDefaultExecutePageInitSetting();
  }

  applyDefaultExecutePageInitSetting = () => {
    const openAsParameter: IActionParameter | undefined = this.getParameterWithName("openAs");
    const executePageInitActionsAfterCloseParam = this.props.parameters.find(
      (x: IActionParameter) => x.name === "executePageInitActionsAfterClose"
    );

    const openAsValue: string = openAsParameter?.symbol?.fixedValue;
    const shouldForceExecutePageInitActions = openAsValue === "newTab";

    if (!shouldForceExecutePageInitActions) return;

    const executeValue: boolean | string | undefined | null = executePageInitActionsAfterCloseParam?.symbol?.fixedValue;
    const isExecuteEnabled: boolean = executeValue === true || executeValue === "true";

    if (!isExecuteEnabled && executePageInitActionsAfterCloseParam) {
      this.updateExecutePageInitActionsAfterCloseParam(true);
    }
  };

  handleGoToScreen = (screenId: string) => {
    this.props.sd.saveScreen(true).catch(() => {});
    this.props.sd.loadScreen(screenId);
  };

  deleteTargetScreenInputParams = (screenInputName: string) => {
    if (this.props.deleteTargetScreenInputParams) {
      this.props.deleteTargetScreenInputParams(screenInputName);
    }
  };

  getScreenDesignContextProviderValues = () => {
    return this.props.sd;
  };

  updateValues = (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => {
    if (this.props.updateValues !== undefined) {
      if (value.actionParameters !== undefined && value.actionParameters) {
        const selectedDisplayItem = this.props.sd.selectedKuikaIds[0];
        const navigationOpenAsParameter = value.actionParameters?.find((x) => x.name === "openAs");
        const existingNavigationOpenAsParameter = this.props.parameters?.find((x) => x.name === "openAs");
        if (navigationOpenAsParameter?.symbol?.fixedValue !== existingNavigationOpenAsParameter?.symbol?.fixedValue) {
          const existingNavKuikaIdParameter = this.props.parameters?.find(
            (x) =>
              x.name === "navigationKuikaId" &&
              x.symbol &&
              x.symbol.fixedValue !== undefined &&
              x.symbol.fixedValue !== ""
          );
          if (existingNavKuikaIdParameter !== undefined) {
            this.props.sd.deleteItemByKuikaId(existingNavKuikaIdParameter.symbol.fixedValue, selectedDisplayItem);
          }
        }
        value.actionParameters.forEach((parameter) => {
          if (
            parameter?.name === "navigationKuikaId" &&
            parameter?.symbol.fixedValue &&
            parameter?.symbol.fixedValue !== ""
          ) {
            // fill the navigation component properties
            const existingNavKuikaIdParameter = value.actionParameters?.find(
              (x) =>
                x.name === "navigationKuikaId" &&
                x.symbol &&
                x.symbol.fixedValue !== undefined &&
                x.symbol.fixedValue !== ""
            );
            if (
              existingNavKuikaIdParameter !== undefined &&
              existingNavKuikaIdParameter.symbol.fixedValue !== parameter?.symbol.fixedValue
            ) {
              this.props.sd.deleteItemByKuikaId(existingNavKuikaIdParameter.symbol.fixedValue);
            }
            const kuikaId = parameter?.symbol.fixedValue;
            let navDisplayItem: IDisplayItem | undefined;
            if (typeof kuikaId === "string" && kuikaId?.startsWith("toBeCreated_")) {
              const compId = kuikaId.replace("toBeCreated_", "");
              navDisplayItem = this.props.sd.addNavigationComponent(compId);

              if (navDisplayItem) {
                parameter.symbol.fixedValue = navDisplayItem.id;
              }
            } else {
              navDisplayItem = this.props.sd.screenDesignDictionary[parseInt(kuikaId, 10)];
              if (navDisplayItem === undefined) {
                const navigationOpenAsParameter = value.actionParameters?.find((x) => x.name === "openAs");
                if (
                  navigationOpenAsParameter?.symbol.fixedValue !== undefined &&
                  navigationOpenAsParameter?.symbol.fixedValue !== "currentPage" &&
                  navigationOpenAsParameter?.symbol.fixedValue !== "newTab" &&
                  navigationOpenAsParameter?.symbol.fixedValue !== "newWindow" &&
                  // KOA-8559: "currentOverlay" is a reserved keyword, not a navigation
                  // component id — it must not spawn a Modal/Drawer/FloatPanel component.
                  navigationOpenAsParameter?.symbol.fixedValue !== "currentOverlay"
                ) {
                  const navComponentId = navigationOpenAsParameter?.symbol.fixedValue;
                  navDisplayItem = this.props.sd.addNavigationComponent(navComponentId);
                }
              }
            }
            if (navDisplayItem) {
              const toScreenParameter = value.actionParameters?.find((x) => x.name === "toScreenId");
              const targetSdId = toScreenParameter?.symbol.fixedValue;
              const targetSdName = this.props.sd.screenList?.find((s) => s.id === targetSdId);
              const navScreenInfo: INavScreenInfo = {
                targetScreenId: targetSdId,
                targetScreenName: targetSdName ? targetSdName.name : ""
              };
              navDisplayItem.navScreenInfo = navScreenInfo;
            }
          }
        });
      }
      this.props.updateValues(value, changeReason);
    }
  };

  getParameterWithName = (
    name: "toScreenId" | "toUrl" | "openAs" | "navigationKuikaId"
  ): IActionParameter | undefined => {
    return this.props.parameters.find((x) => x.name === name);
  };

  getToScreenSelectValue = () => {
    const toScreenId = this.getParameterWithName("toScreenId");
    const toUrl = this.getParameterWithName("toUrl");
    if (toUrl?.symbol?.fixedValue && toUrl.symbol.fixedValue !== "") return toUrl.symbol.fixedValue;
    if (toScreenId?.symbol?.fixedValue && toScreenId.symbol.fixedValue !== "") return toScreenId.symbol.fixedValue;
    return "";
  };

  updateToURL = (url: string) => {
    const parameters = [...this.props.parameters];
    const toScreenParameter = parameters.find((x) => x.name === "toScreenId");
    const toUrlParameter = parameters.find((x) => x.name === "toUrl");
    if (toScreenParameter && toUrlParameter) {
      toScreenParameter.symbol.fixedValue = "";
      toUrlParameter.symbol.fixedValue = url;
    }
    this.updateValues({ actionParameters: parameters });
  };

  renderToScreenId = (parameter: IActionParameter): JSX.Element => {
    if (this.props.action.label === "NavigateToTask") return <></>;

    const parameters = [...this.props.parameters];
    let targetScreenId = "";
    const toScreenParameter = parameters.find((x) => x.name === "toScreenId");
    if (toScreenParameter) {
      targetScreenId = toScreenParameter.symbol.fixedValue;
    }
    const processStepScreens = this.props.sd.screenList?.filter((screen) =>
      this.props.sd.project.processStepScreenIds?.find((process) => screen.id === process.screenId.toString())
    );

    const screenOptions: KuikaSelectboxOption[] =
      this.props.sd.screenList
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.filter((x) => x.publicMetaData.screenType !== EScreenType.Report)
        ?.filter((x) => x.publicMetaData.screenType !== EScreenType.Master)
        ?.filter((x) => x.publicMetaData.screenType !== EScreenType.Component)
        ?.filter((x) => x.id !== processStepScreens?.find((p) => p.id === x.id)?.id)
        ?.map((s): KuikaSelectboxOption => ({
          key: s.id,
          value: s.id,
          label: s.name,
          testId: `navigateToScreenOption-${s.name}`
        })) ?? [];

    return (
      <>
        <div
          className="kuika_add_actions_properties_name"
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <span>To Screen</span>
          {targetScreenId && Guid.isGuid(targetScreenId) && (
            <span
              style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
              onClick={() => this.handleGoToScreen(targetScreenId)}
            >
              <KuikaSvg name="external-link" width={16} height={16} color="var(--dark-7)" />
            </span>
          )}
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            data-testid="navigateToScreenSelect"
            style={{ width: "100%" }}
            value={this.getToScreenSelectValue()}
            onChange={(value: string): void => this.onSelectToScreen(value, false)}
            className="kuika_add_actions_inputs_size"
            options={screenOptions}
          />
        </div>

        {parameter.symbol.fixedValue && Guid.isGuid(parameter.symbol.fixedValue) && (
          <NavigationScreenInputPicker
            action={this.props.action}
            deleteTargetScreenInputParams={this.deleteTargetScreenInputParams}
            targetScreenId={targetScreenId}
            targetScreenInputParams={this.props.targetScreenInputParams}
            updateTargetScreenInputParams={(targetScreenInputParams, changeReason?: ESdChangeReason) =>
              this.updateValues({ targetScreenInputParams }, changeReason)
            }
          />
        )}
      </>
    );
  };

  renderToStepKey = (parameter: IActionParameter): JSX.Element => {
    return (
      <div key={Guid.create().toString()}>
        <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
          <ActionParameterTitle
            parameter={{ name: "Step Key", Typename: parameter.Typename, cardinality: parameter.cardinality }}
          />
        </span>
        <SymbolPicker
          changeType="onBlur"
          key={Guid.create().toString()}
          symbol={parameter.symbol}
          valueType={parameter.Typename}
          targetCardinality={parameter.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) => {
            const parameters = [...this.props.parameters];
            const stepKeyParameter = parameters.find((x) => x.name === "WorkflowStepInstanceId");
            if (stepKeyParameter) {
              stepKeyParameter.symbol = symbol;
            }
            this.updateValues({ actionParameters: parameters }, changeReason);
          }}
          data_testid="stepKey"
          bindingParameter={parameter}
          bindingLabel="Step Key"
        />
      </div>
    );
  };

  onSelectToScreen = (val: string, check?: boolean): void => {
    const parameters = [...this.props.parameters];
    const toScreenParameter = parameters.find((x) => x.name === "toScreenId");
    const toUrlParameter = parameters.find((x) => x.name === "toUrl");
    if (toScreenParameter && toUrlParameter) {
      toScreenParameter.symbol.fixedValue = val;
      toUrlParameter.symbol.fixedValue = "";
      const targetScreenInputParams = [] as ITargetScreenInputParam[];
      const toScreenData = this.props.sd.screenList.find((x) => x.id === val);
      if (toScreenData) {
        const targetScreenInputParamsProps = _.cloneDeep(this.props.targetScreenInputParams);
        toScreenData?.publicMetaData.screenInputs?.forEach((s) => {
          if (
            !targetScreenInputParams.some(
              (x) => x.name === s.name && x.typeName === s.type && x.cardinality === s.cardinality
            )
          ) {
            const foundParam = _.cloneDeep(
              targetScreenInputParamsProps?.find(
                (y) =>
                  y.name === s.name && (y.type === s.type || y.typeName === s.type) && y.cardinality === s.cardinality
              )
            );
            if (foundParam) {
              targetScreenInputParams.push(foundParam);
            } else {
              targetScreenInputParams.push({
                name: s.name,
                typeName: s.type,
                cardinality: s.cardinality,
                symbol: {
                  description: s.name,
                  category: ESymbolCategory.fixed,
                  fixedValue: ""
                } as ISymbol
              } as ITargetScreenInputParam);
            }
          }
        });
      }
      this.updateValues({ actionParameters: parameters, targetScreenInputParams });
    }
  };

  getNavigationComponentList = () => {
    const navComps = this.props.sd.project.componentSetObj.components.filter((x) => x.isItNavigationComponent === true);

    // FloatPanel used to be filtered out here as web-only for Mobile projects. RNM-0107 emits KFloatPanel and
    // RNM-0113 gave it the same overlay behaviour Modal and Drawer have, so a mobile project can target all three.
    return navComps;
  };

  getOpenAsSelectValue = () => {
    const openAs = this.getParameterWithName("openAs");
    if (openAs) {
      return openAs.symbol.fixedValue;
    }

    if (this.props.action.label === "NavigateToTask") {
      return "currentPage";
    }

    return "";
  };

  renderOpenAs = (parameter: IActionParameter): JSX.Element => {
    const executePageInitActionsAfterCloseParam = this.props.parameters.find(
      (x) => x.name === "executePageInitActionsAfterClose"
    );
    const executePageInitActionsAfterCloseLbl = "Execute Page Init Actions After Closed";
    const disableSmartExecutePageInitParam = this.props.parameters.find(
      (x) => x.name === "disableSmartExecutePageInit"
    );
    const disableSmartExecutePageInitLbl: string = "Disable Smart Execute Page Init";
    const openAsValue: string = this.getOpenAsSelectValue();
    // KOA-8559: "currentOverlay" swaps the open overlay's content in place — it never
    // closes anything — so "Execute Page Init Actions After Closed" is a no-op for it
    // (navigate() ignores it). Hide it, same as newTab which also has no close event.
    const shouldHideExecutePageInitOptions: boolean = openAsValue === "newTab" || openAsValue === "currentOverlay";
    // The reserved keywords are tagged by their value; the navigation components below by
    // their name ("Drawer" / "Modal" / "FloatPanel"), because their VALUE is a per-project
    // component guid that no test could know in advance.
    const openAsOptions: KuikaSelectboxOption[] = [
      { key: "currentPage", value: "currentPage", label: "Current Page", testId: "navigateOpenAsOption-currentPage" }
    ];
    // A second browser tab is an affordance mobile does not have: one app, one surface. Opening
    // another SCREEN of the same app alongside the current one has no phone equivalent, and an
    // external link is a different action entirely (OpenInBrowser / OpenInWebView), so this is
    // hidden for Mobile projects rather than degraded. "newWindow" needs no gate — it is never
    // offered here, only tolerated in the guards below for configurations saved elsewhere.
    if (this.props.sd.project.projectType !== EProjectType.Mobile) {
      openAsOptions.push({ key: "newTab", value: "newTab", label: "New Tab", testId: "navigateOpenAsOption-newTab" });
    }
    // KOA-8559: "Current Overlay" renders the target inside the already-open
    // drawer/modal/floatingPanel instead of routing the page behind it. React Native gained it in
    // RNM-0113 (OverlayHost holds the overlay's screen stack), so it is no longer web-only.
    openAsOptions.push({
      key: "currentOverlay",
      value: "currentOverlay",
      label: "Current Overlay",
      testId: "navigateOpenAsOption-currentOverlay"
    });
    this.getNavigationComponentList().forEach((nc): void => {
      openAsOptions.push({
        key: nc.id,
        value: nc.id,
        label: nc.name,
        testId: `navigateOpenAsOption-${nc.name}`
      });
    });

    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Open As:
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            size="sm"
            style={{ width: "100%" }}
            data-testid="navigateOpenAsSelect"
            value={openAsValue ?? null}
            onChange={(value: string): void => this.onSelectOpenAs(value)}
            className="kuika_add_actions_inputs_size"
            options={openAsOptions}
          />
          {executePageInitActionsAfterCloseParam && !shouldHideExecutePageInitOptions && (
            <div className="kuika_switch_property" style={{ height: 52, paddingTop: 20 }}>
              <div style={{ float: "left" }}>
                <KuikaTooltip content={executePageInitActionsAfterCloseLbl}>
                  {/* The kit's tooltip anchors to its child's DOM node, and this label is a class
                      component, which has none to give. The span is that node. */}
                  <span>
                    <KuikaSmallMediumText
                      style={{
                        width: 180,
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        display: "inline-block"
                      }}
                    >
                      {executePageInitActionsAfterCloseLbl}
                    </KuikaSmallMediumText>
                  </span>
                </KuikaTooltip>
              </div>
              <div style={{ float: "right" }}>
                <KuikaSwitch
                  dataTestId="executePageInitActionsAfterCloseSwitch"
                  onChange={this.updateExecutePageInitActionsAfterCloseParam}
                  checked={executePageInitActionsAfterCloseParam.symbol?.fixedValue ?? false}
                />
              </div>
            </div>
          )}
          {disableSmartExecutePageInitParam &&
            !shouldHideExecutePageInitOptions &&
            executePageInitActionsAfterCloseParam?.symbol?.fixedValue?.toString?.() === "true" && (
              <div className="kuika_switch_property" style={{ height: 52 }}>
                <div style={{ float: "left" }}>
                  <KuikaTooltip content={disableSmartExecutePageInitLbl}>
                    <span>
                      <KuikaSmallMediumText
                        style={{
                          width: 180,
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          display: "inline-block"
                        }}
                      >
                        {disableSmartExecutePageInitLbl}
                      </KuikaSmallMediumText>
                    </span>
                  </KuikaTooltip>
                </div>
                <div style={{ float: "right" }}>
                  <KuikaSwitch
                    onChange={this.updateDisableSmartExecutePageInitParam}
                    checked={disableSmartExecutePageInitParam?.symbol?.fixedValue ?? false}
                  />
                </div>
              </div>
            )}
        </div>
      </>
    );
  };

  updateExecutePageInitActionsAfterCloseParam = (checked: boolean) => {
    const params = _.cloneDeep(this.props.parameters);
    const executePageInitActionsAfterCloseParam = params.find((x) => x.name === "executePageInitActionsAfterClose");
    if (!executePageInitActionsAfterCloseParam) return;
    executePageInitActionsAfterCloseParam.symbol.fixedValue = checked;

    let disableSmartExecutePageInitParam = params.find((x) => x.name === "disableSmartExecutePageInit");
    if (disableSmartExecutePageInitParam) {
      disableSmartExecutePageInitParam.symbol.fixedValue = false;
    } else {
      disableSmartExecutePageInitParam = {
        name: "disableSmartExecutePageInit",
        Typename: "Boolean",
        cardinality: ECardinality.One,
        symbol: KMainFunctions.getDefaultSymbol("Boolean")
      };
      disableSmartExecutePageInitParam.symbol.fixedValue = false;
      params.push(disableSmartExecutePageInitParam);
    }

    this.updateValues({ actionParameters: params });
  };

  updateDisableSmartExecutePageInitParam = (checked: boolean) => {
    const params = _.cloneDeep(this.props.parameters);
    let disableSmartExecutePageInitParam = params.find((x) => x.name === "disableSmartExecutePageInit");

    if (!disableSmartExecutePageInitParam) {
      disableSmartExecutePageInitParam = {
        name: "disableSmartExecutePageInit",
        Typename: "Boolean",
        cardinality: ECardinality.One,
        symbol: KMainFunctions.getDefaultSymbol("Boolean")
      };
      disableSmartExecutePageInitParam.symbol.fixedValue = checked;
      params.push(disableSmartExecutePageInitParam);
      this.updateValues({ actionParameters: params });
      return;
    }

    disableSmartExecutePageInitParam.symbol.fixedValue = checked;
    this.updateValues({ actionParameters: params });
  };

  onSelectOpenAs = (val: string): void => {
    const params = _.cloneDeep(this.props.parameters);
    const openAsParameter = params.find((x) => x.name === "openAs");
    const navigationKuikaIdParameter = params.find((x) => x.name === "navigationKuikaId");

    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;

    if (openAsParameter) {
      openAsParameter.symbol.fixedValue = val;
      const isNavigationComponentSelected = Guid.isGuid(val);
      if (isNavigationComponentSelected) {
        navigationKuikaIdParameter!.symbol.fixedValue = `toBeCreated_${val}`;
      } else {
        navigationKuikaIdParameter!.symbol.fixedValue = null;
      }
      this.updateValues(valuesToUpdate);
    }
  };

  renderNavigationKuikaId = (parameter: IActionParameter): JSX.Element => {
    const openAs = this.getParameterWithName("openAs");
    const val = openAs?.symbol?.fixedValue;
    // KOA-8559: "currentOverlay" reuses the already-open overlay, so (like the other
    // reserved keywords) it has no navigation-component target to pick.
    if (val === "currentPage" || val === "newTab" || val === "newWindow" || val === "currentOverlay") return <></>;
    const navKuikaId = this.getParameterWithName("navigationKuikaId")?.symbol.fixedValue;
    return <PropertyForm kuikaId={navKuikaId} location="NavigationPanel" isCalledFromActionPicker={true} />;
  };

  render() {
    if (this.props.parameters.length <= 0) return null;

    const stepKeyParameter = this.props.parameters.find((x) => x.name === "WorkflowStepInstanceId");
    const toScreenIdParameter = this.props.parameters.find((x) => x.name === "toScreenId");
    const openAsParameter = this.props.parameters.find((x) => x.name === "openAs");
    const navigationKuikaIdParameter = this.props.parameters.find((x) => x.name === "navigationKuikaId");

    return (
      <div style={{ width: "100%" }}>
        {this.props.action.label === "NavigateToTask" &&
          stepKeyParameter !== undefined &&
          this.renderToStepKey(stepKeyParameter)}

        {this.props.action.label === "NAVIGATE" &&
          toScreenIdParameter !== undefined &&
          this.renderToScreenId(toScreenIdParameter)}

        {openAsParameter !== undefined && this.renderOpenAs(openAsParameter)}

        {navigationKuikaIdParameter !== undefined && this.renderNavigationKuikaId(navigationKuikaIdParameter)}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const navigationSymbolsGroup = withContext<INavigationSymbolsGroupProps>(
  withSD<INavigationSymbolsGroupProps>(NavigationSymbolsGroup)
);
export { navigationSymbolsGroup as NavigationSymbolsGroup };
